import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/pix`;

/** Campo EMV: id + tamanho (2 dígitos, em bytes UTF-8) + valor */
const tlv = (id, value) => {
  const text = String(value ?? "");
  const length = new TextEncoder().encode(text).length;
  return `${id}${String(length).padStart(2, "0")}${text}`;
};

/** CRC16/CCITT-FALSE (poly 0x1021), o mesmo da API */
const crc16 = (payload) => {
  let crc = 0xffff;
  new TextEncoder().encode(payload).forEach((byte) => {
    crc ^= byte << 8;
    for (let i = 0; i < 8; i += 1) {
      crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1;
      crc &= 0xffff;
    }
  });
  return crc.toString(16).toUpperCase().padStart(4, "0");
};

/**
 * Campos 59/60 do QR: sem acentos e espaços extras; acima do limite,
 * corta na última palavra inteira (PixDataHelper.normalizeText da API)
 */
const normalizeEmvText = (value, max) => {
  const text = String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= max) return text;
  const lastSpace = text.lastIndexOf(" ", max);
  return (lastSpace > 0 ? text.slice(0, lastSpace) : text.slice(0, max)).trim();
};

/**
 * PIX Service
 * Gerencia todas as operações de PIX gerados
 */
class PixService {
  /**
   * Get all PIX generated
   * @param {object} [options] - config extra do axios (ex.: { silent: true })
   * @returns {Promise<Array>}
   */
  async getAllPix(options = {}) {
    try {
      const response = await api.get(API_BASE, options);
      return unwrapList(response);
    } catch (error) {
      console.error("❌ Erro ao buscar PIX:", error);
      throw error;
    }
  }

  /**
   * Buscar pagamento PIX pelo txid
   * @param {string} txid - Transaction ID
   * @returns {Promise<object>} { payload, qrcodeUrl, txid, amount, status, ... }
   */
  async getPixByTxid(txid) {
    try {
      const response = await api.get(`${API_BASE}/${txid}`);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar PIX ${txid}:`, error);
      throw error;
    }
  }

  /**
   * Criar PIX (gera payload EMV e QR code quando pay_method é PIX)
   * @param {object} pixData - { amount, description, name, contact, order_code, pay_method, txid? }
   * @returns {Promise<object>} { payload, qrcodeUrl, txid, ... }
   */
  async createPix(pixData) {
    try {
      const response = await api.post(`${API_BASE}/create`, pixData);
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar PIX:", error);
      throw error;
    }
  }

  /**
   * Confirmar pagamento PIX (método legado): só marca status = 'S',
   * sem atualizar o pedido. Use para PIX sem pedido vinculado.
   * @param {string} txid - Transaction ID
   * @returns {Promise<object>}
   */
  async confirmPix(txid) {
    try {
      const response = await api.post(
        `${API_BASE}/confirm/${txid}`,
        {}
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao confirmar PIX ${txid}:`, error);
      throw error;
    }
  }

  /**
   * Marcar PIX como recebido: grava valor e método recebidos e marca o
   * pedido vinculado como pago. O backend exige value_received, pay_method
   * e um pedido (orderCode) vinculado ao PIX.
   * @param {string} txid - Transaction ID
   * @param {object} receivedData - { value_received, pay_method }
   * @returns {Promise<object>}
   */
  async markAsReceived(txid, receivedData) {
    this.validateReceivedData(receivedData);
    try {
      const response = await api.put(
        `${API_BASE}/${txid}/received`,
        receivedData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao marcar PIX ${txid} como recebido:`, error);
      throw error;
    }
  }

  /**
   * Confirma o pagamento de um pedido.
   * Usa PUT /pix/{code}/received com o código do pedido no lugar do txid:
   * o backend aceita código de até 8 caracteres como código de pedido e,
   * se o pedido ainda não tem PIX (dinheiro/cartão), cria um único registro.
   * @param {string} orderCode - Order code
   * @param {object} paymentData - { value_received, pay_method, ... }
   * @returns {Promise<object>}
   */
  async confirmPayment(orderCode, paymentData) {
    this.validateReceivedData(paymentData);
    try {
      const { orderCode: _ignored, company_code, ...body } = paymentData || {};
      const response = await api.put(
        `${API_BASE}/${orderCode}/received`,
        body
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(
        `❌ Erro ao confirmar pagamento do pedido ${orderCode}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * O backend exige value_received e pay_method (senão 400)
   * @param {object} data
   * @throws {Error} se faltar algum campo
   */
  validateReceivedData(data) {
    const value = Number(data?.value_received);
    if (!data || !Number.isFinite(value) || value <= 0) {
      throw new Error("Informe o valor recebido.");
    }
    if (!String(data.pay_method || "").trim()) {
      throw new Error("Informe a forma de pagamento.");
    }
  }

  /**
   * Delete PIX
   * @param {string} txid - Transaction ID
   * @returns {Promise<void>}
   */
  async deletePix(txid) {
    try {
      await api.delete(`${API_BASE}/${txid}`);
    } catch (error) {
      console.error(`❌ Erro ao deletar PIX ${txid}:`, error);
      throw error;
    }
  }

  /**
   * Status do PIX: 'S' = pago, demais = pendente
   * @param {object} pix
   * @returns {boolean}
   */
  isPaid(pix) {
    const status = String(pix?.status ?? "").toUpperCase();
    return status === "S" || status === "RECEIVED" || pix?.recebido === true;
  }

  /**
   * Valor cobrado (BigDecimal chega como número ou texto)
   * @param {object} pix
   * @returns {number}
   */
  getAmount(pix) {
    const value = Number(pix?.amount ?? pix?.valor ?? 0);
    return Number.isFinite(value) ? value : 0;
  }

  /**
   * Valor efetivamente recebido; PIX confirmado sem value_received
   * (POST /pix/confirm) conta pelo valor cobrado
   * @param {object} pix
   * @returns {number}
   */
  getReceivedAmount(pix) {
    if (!this.isPaid(pix)) return 0;
    const value = Number(pix?.value_received);
    return Number.isFinite(value) && value > 0 ? value : this.getAmount(pix);
  }

  /**
   * LocalDateTime da API: ISO ("2025-12-07T15:30:00") ou array
   * [ano, mês, dia, hora, min, seg] quando serializado como timestamp
   * @param {string|Array} value
   * @returns {Date|null}
   */
  parseDate(value) {
    if (!value) return null;
    const date = Array.isArray(value)
      ? new Date(value[0], (value[1] || 1) - 1, value[2] || 1, value[3] || 0, value[4] || 0, value[5] || 0)
      : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  /**
   * Telefone do contato no formato do WhatsApp (55 + DDD + número);
   * null quando o contato é e-mail ou não tem DDD
   * @param {string} contact
   * @returns {string|null}
   */
  getWhatsAppNumber(contact) {
    const raw = String(contact || "").trim();
    if (!raw || raw.includes("@")) return null;
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10 || digits.length === 11) return `55${digits}`;
    if ((digits.length === 12 || digits.length === 13) && digits.startsWith("55")) {
      return digits;
    }
    return null;
  }

  /**
   * Tipo da chave PIX cadastrada na empresa (para exibição)
   * @param {string} key
   * @returns {string}
   */
  getPixKeyType(key) {
    const value = String(key || "").trim();
    if (!value) return "";
    if (value.includes("@")) return "E-mail";
    if (value.startsWith("+")) return "Telefone";
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
      return "Aleatória";
    }
    const digits = value.replace(/\D/g, "");
    if (digits.length === 14) return "CNPJ";
    if (digits.length === 11) return "CPF";
    return "Chave";
  }

  /**
   * A empresa tem chave, nome e cidade do recebedor (mesma regra do
   * PixDataHelper.isConfigured da API)
   * @param {object} company - CompanyResponseDTO
   * @returns {boolean}
   */
  isCompanyPixConfigured(company) {
    if (!company) return false;
    if (typeof company.pixConfigured === "boolean") return company.pixConfigured;
    return Boolean(
      String(company.pixKey || "").trim() &&
        String(company.pixMerchantCity || "").trim() &&
        String(company.pixMerchantName || company.name || "").trim()
    );
  }

  /**
   * BR Code estático (sem valor) da empresa, para o QR do balcão: o
   * cliente digita o valor no app do banco. Segue o mesmo layout EMV do
   * PixServiceImpl.buildPixPayload da API, sem o campo 54 (valor) e com
   * txid "***" (Manual BR Code do Bacen para QR estático).
   * @param {object} company - CompanyResponseDTO (pixKey, pixMerchantName, pixMerchantCity)
   * @returns {string|null}
   */
  buildStaticPayload(company) {
    if (!this.isCompanyPixConfigured(company)) return null;
    const key = String(company.pixKey).trim();
    const name = normalizeEmvText(company.pixMerchantName || company.name, 25);
    const city = normalizeEmvText(company.pixMerchantCity, 15);

    const payload =
      tlv("00", "01") +
      tlv("26", tlv("00", "BR.GOV.BCB.PIX") + tlv("01", key)) +
      tlv("52", "0000") +
      tlv("53", "986") +
      tlv("58", "BR") +
      tlv("59", name) +
      tlv("60", city) +
      tlv("62", tlv("05", "***")) +
      "6304";
    return payload + crc16(payload);
  }

  /**
   * Valida o formulário de criação de PIX
   * @param {object} data
   * @returns {object} erros por campo
   */
  validatePixData(data) {
    const errors = {};
    const amount = Number(String(data.amount ?? "").replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) {
      errors.amount = "Informe um valor maior que zero";
    }
    if (!String(data.pay_method || "").trim()) {
      errors.pay_method = "Selecione a forma de pagamento";
    }
    // Contato aceita telefone ou e-mail
    const contact = String(data.contact || "").trim();
    const digits = contact.replace(/\D/g, "");
    if (
      contact &&
      !contact.includes("@") &&
      (digits.length < 10 || digits.length > 13)
    ) {
      errors.contact = "Informe um e-mail ou DDD + número (10 a 13 dígitos)";
    }
    return errors;
  }

  /**
   * Format currency value
   * @param {number} value - Value to format
   * @returns {string}
   */
  formatCurrency(value) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  }

  /**
   * Format date
   * @param {string} date - Date string
   * @returns {string}
   */
  formatDate(date) {
    const parsed = this.parseDate(date);
    if (!parsed) return "-";
    return parsed.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /**
   * Copy text to clipboard. Fora de HTTPS o navigator.clipboard não
   * existe: cai no execCommand("copy") com um textarea temporário.
   * @param {string} text - Text to copy
   * @returns {Promise<void>}
   */
  async copyToClipboard(text) {
    if (!text) throw new Error("Nada para copiar");
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
      }
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (!ok) throw new Error("Não foi possível copiar");
    } catch (error) {
      console.error("❌ Erro ao copiar para área de transferência:", error);
      throw error;
    }
  }

  /**
   * Format phone number with DDD mask
   * @param {string} phone - Phone number
   * @returns {string}
   */
  formatPhone(phone) {
    if (!phone) return "-";

    // Remove tudo que não é dígito
    const cleaned = phone.replace(/\D/g, "");

    // Verifica se tem DDD (11 dígitos) ou não (9 dígitos)
    if (cleaned.length === 11) {
      // Formato: (11) 91234-5678
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(
        2,
        7,
      )}-${cleaned.substring(7)}`;
    } else if (cleaned.length === 10) {
      // Formato: (11) 1234-5678
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(
        2,
        6,
      )}-${cleaned.substring(6)}`;
    } else if (cleaned.length === 9) {
      // Sem DDD: 91234-5678
      return `${cleaned.substring(0, 5)}-${cleaned.substring(5)}`;
    } else if (cleaned.length === 8) {
      // Sem DDD: 1234-5678
      return `${cleaned.substring(0, 4)}-${cleaned.substring(4)}`;
    }

    return phone;
  }
}

export default new PixService();
